'use client'

import {
   Box,
   Button,
   Card,
   Container,
   Divider,
   Group,
   Select,
   SimpleGrid,
   Stack,
   Text,
   Textarea,
   TextInput
} from '@blocks'
import {
   cleanParkMapEmbedSource,
   enquiryResponseExpectation,
   primaryCallToActionLabel,
   ROUTE_HERO_BANNER_DESKTOP_MEDIA_QUERY,
   siteAccentHex,
   sitePrimaryDeepHex,
   siteSectionHeadingHex,
   siteTextOnDarkBandHex,
   siteTextOnDarkBandMutedHex,
   siteTextOnDarkBandSubtleHex,
   siteTypographyEyebrowFontSizeDesktop,
   siteTypographyEyebrowFontSizeMobile,
   siteTypographySectionLeadFontSizeDesktop,
   siteTypographySectionLeadFontSizeMobile,
   siteTypographySectionTitlePrimaryFontSizeDesktop,
   siteTypographySectionTitlePrimaryFontSizeMobile
} from '@constants'
import {useMediaQuery} from '@hooks'
import {IconMapPin, IconPhone, IconSend} from '@icons'
import {useForm} from '@mantine/form'
import {notifications} from '@mantine/notifications'
import {usePathname} from 'next/navigation'
import {useState} from 'react'
import {RoutePageHero} from '@/blocks/site/RoutePageHero'

type FormValues = {
   fullName: string
   email: string
   phone: string
   topic: string | null
   message: string
}

type ContactPayload = Readonly<{
   /** Pre-filled topic from `/contact?topic=` — set by the server page. */
   initialTopic?: string | null
}>

const ContactContent = ({initialTopic = null}: ContactPayload) => {
   const pathname = usePathname()
   const isContactPage = pathname === '/contact'
   const isDesktop = useMediaQuery(
      ROUTE_HERO_BANNER_DESKTOP_MEDIA_QUERY
   )

   const [submitting, setSubmitting] = useState(false)

   const form = useForm<FormValues>({
      initialValues: {
         fullName: '',
         email: '',
         phone: '',
         topic: initialTopic,
         message: ''
      },
      validate: {
         fullName: (value) =>
            value.trim().length < 2
               ? 'Please enter your full name'
               : null,
         email: (value) =>
            /^\S+@\S+\.\S+$/.test(value)
               ? null
               : 'Please enter a valid email address',
         topic: (value) => (!value ? 'Please select a topic' : null),
         message: (value) =>
            value.trim().length < 10
               ? 'Please enter a message (at least 10 characters)'
               : null
      }
   })

   const handleSubmit = async (values: FormValues) => {
      setSubmitting(true)
      try {
         const response = await fetch('/api/contact', {
            method: 'POST',
            headers: {'content-type': 'application/json'},
            body: JSON.stringify(values)
         })
         const data: {ok: boolean; message?: string} =
            await response.json()
         if (!response.ok || !data.ok) {
            notifications.show({
               title: 'Could not send',
               message:
                  data.message ??
                  'Please try again in a moment or call us on 07 889 6823.',
               color: 'red'
            })
            return
         }
         notifications.show({
            title: 'Message sent',
            message:
               data.message ??
               'We will get back to you as soon as we can.',
            color: 'teal'
         })
         form.reset()
      } catch {
         notifications.show({
            title: 'Could not send',
            message: 'Please try again or phone 07 889 6823.',
            color: 'red'
         })
      } finally {
         setSubmitting(false)
      }
   }

   return (
      <Stack
         bg={'primary.9'}
         miw={0}
         maw={'100%'}
         gap={isContactPage ? 0 : undefined}
         pb={isDesktop ? 220 : 112}
         pt={isContactPage ? 0 : isDesktop ? 220 : 112}
      >
         {isContactPage ? (
            <RoutePageHero headline={primaryCallToActionLabel} />
         ) : null}

         <Container size={'xl'}>
            <Stack
               gap={isDesktop ? 96 : 48}
               pt={isContactPage ? (isDesktop ? 96 : 48) : undefined}
               pb={isContactPage ? (isDesktop ? 120 : 56) : undefined}
            >
               <Stack
                  align={'center'}
                  gap={20}
               >
                  <Text
                     c={siteTextOnDarkBandHex}
                     fz={
                        isDesktop
                           ? siteTypographyEyebrowFontSizeDesktop
                           : siteTypographyEyebrowFontSizeMobile
                     }
                     fw={700}
                     tt={'uppercase'}
                     lts={2.5}
                     lh={1}
                     ta={'center'}
                  >
                     Contact
                  </Text>
                  {!isContactPage ? (
                     <Text
                        id={'contact-primary-heading'}
                        c={siteAccentHex}
                        fz={
                           isDesktop
                              ? siteTypographySectionTitlePrimaryFontSizeDesktop
                              : siteTypographySectionTitlePrimaryFontSizeMobile
                        }
                        fw={800}
                        lh={1.1}
                        ta={'center'}
                        maw={700}
                     >
                        {primaryCallToActionLabel}
                     </Text>
                  ) : null}
               </Stack>

               <SimpleGrid
                  cols={{base: 1, md: 2}}
                  spacing={isDesktop ? 72 : 'xl'}
                  style={{alignItems: 'flex-start'}}
               >
                  <Stack
                     mt={isDesktop ? 20 : 0}
                     gap={28}
                  >
                     <Text
                        c={siteTextOnDarkBandMutedHex}
                        fz={
                           isDesktop
                              ? siteTypographySectionLeadFontSizeDesktop
                              : siteTypographySectionLeadFontSizeMobile
                        }
                        fw={500}
                        lh={1.7}
                     >
                        Questions about the automatic wash, self-serve
                        bays, dog wash, vacuums, detailing, or fleet
                        accounts? Send a short message — we read
                        everything that comes through.
                     </Text>
                     <Text
                        c={siteTextOnDarkBandMutedHex}
                        fz={
                           isDesktop
                              ? siteTypographySectionLeadFontSizeDesktop
                              : siteTypographySectionLeadFontSizeMobile
                        }
                        fw={500}
                        lh={1.7}
                     >
                        {enquiryResponseExpectation} You are also
                        welcome to visit us at 42 Studholme Street or
                        call{' '}
                        <Text
                           span
                           fw={600}
                           c={siteTextOnDarkBandHex}
                        >
                           07 889 6823
                        </Text>
                        .
                     </Text>
                     <Divider
                        w={'60px'}
                        mt={4}
                        size={3}
                        color={'rgba(255,255,255,0.35)'}
                     />

                     <Group
                        align={'flex-start'}
                        gap={14}
                        wrap={'nowrap'}
                     >
                        <IconMapPin
                           size={24}
                           color={siteAccentHex}
                           style={{
                              marginTop: 4,
                              flexShrink: 0
                           }}
                        />
                        <Stack gap={4}>
                           <Text
                              c={siteTextOnDarkBandHex}
                              fw={700}
                              fz={19}
                           >
                              Visit us
                           </Text>
                           <Text
                              c={siteTextOnDarkBandSubtleHex}
                              fz={18}
                              lh={1.65}
                           >
                              42 Studholme Street
                              <br />
                              Morrinsville 3300
                           </Text>
                        </Stack>
                     </Group>

                     <a
                        href={'tel:078896823'}
                        style={{textDecoration: 'none'}}
                     >
                        <Group
                           align={'flex-start'}
                           gap={14}
                           wrap={'nowrap'}
                        >
                           <IconPhone
                              size={24}
                              color={siteAccentHex}
                              style={{flexShrink: 0, marginTop: 2}}
                           />
                           <Stack gap={4}>
                              <Text
                                 c={siteTextOnDarkBandHex}
                                 fw={700}
                                 fz={19}
                              >
                                 Call us
                              </Text>
                              <Text
                                 c={siteTextOnDarkBandSubtleHex}
                                 fz={18}
                                 lh={1.65}
                              >
                                 07 889 6823
                              </Text>
                           </Stack>
                        </Group>
                     </a>

                     <Box
                        style={{
                           borderRadius: 16,
                           overflow: 'hidden',
                           border: '1px solid rgba(255,255,255,0.2)',
                           boxShadow: '0 8px 28px rgba(0, 0, 0, 0.2)'
                        }}
                     >
                        <iframe
                           title={
                              'Clean Park Morrinsville on Google Maps'
                           }
                           src={cleanParkMapEmbedSource}
                           width={'100%'}
                           height={isDesktop ? 340 : 260}
                           style={{border: 0, display: 'block'}}
                           allowFullScreen={false}
                           loading={'lazy'}
                           referrerPolicy={
                              'no-referrer-when-downgrade'
                           }
                        />
                     </Box>
                  </Stack>

                  <Card
                     p={{base: 20, sm: 28}}
                     radius={'xl'}
                     style={{
                        width: '100%',
                        alignSelf: 'flex-start'
                     }}
                  >
                     <form
                        onSubmit={form.onSubmit((values) => {
                           void handleSubmit(values)
                        })}
                     >
                        <Stack gap={22}>
                           <TextInput
                              required
                              withAsterisk
                              size={'lg'}
                              label={'Name'}
                              {...form.getInputProps('fullName')}
                           />
                           <TextInput
                              required
                              withAsterisk
                              size={'lg'}
                              label={'Email address'}
                              type={'email'}
                              {...form.getInputProps('email')}
                           />
                           <TextInput
                              size={'lg'}
                              label={'Phone (optional)'}
                              {...form.getInputProps('phone')}
                           />
                           <Select
                              required
                              withAsterisk
                              size={'lg'}
                              label={'Topic'}
                              placeholder={'What is this about?'}
                              data={[
                                 {
                                    value: 'car-wash',
                                    label: 'Car wash'
                                 },
                                 {
                                    value: 'detailing',
                                    label: 'Detailing'
                                 },
                                 {
                                    value: 'dog-wash',
                                    label: 'Dog wash'
                                 },
                                 {
                                    value: 'fleet',
                                    label: 'Fleet account'
                                 },
                                 {
                                    value: 'general',
                                    label: 'General enquiry'
                                 }
                              ]}
                              {...form.getInputProps('topic')}
                           />
                           <Textarea
                              required
                              withAsterisk
                              rows={5}
                              size={'lg'}
                              label={'Message'}
                              {...form.getInputProps('message')}
                           />
                           <Group>
                              <Button
                                 type={'submit'}
                                 c={'white'}
                                 fw={700}
                                 color={'primary'}
                                 variant={'gradient'}
                                 size={'lg'}
                                 radius={'xl'}
                                 gradient={{
                                    from: sitePrimaryDeepHex,
                                    to: siteSectionHeadingHex,
                                    deg: 145
                                 }}
                                 loading={submitting}
                                 leftSection={<IconSend size={18} />}
                              >
                                 Send message
                              </Button>
                           </Group>
                        </Stack>
                     </form>
                  </Card>
               </SimpleGrid>
            </Stack>
         </Container>
      </Stack>
   )
}

export const Contact = (payload: ContactPayload) => {
   return <ContactContent {...payload} />
}
